import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  approveSession,
  fetchTestSessionSummary,
  setSessionStatus,
} from "../api";
import {
  Step2VM,
  TestSessionDto,
  UserDto,
  ValimateTestCaseResultDto,
  SessionSummaryVM,
  TestSessionStatusHistoryDto,
  TestSessionCommentDto,
  DeclarationOfConformityDto,
} from "../../autogenerated/models";
import { ApplicationState } from "..";

interface SessionSummaryModal {
  placeholder: string;
  destinationStatus: string;
}

interface SessionSummaryInitial {
  hasFilesToDownload: boolean;
  choices: Step2VM;
  user: UserDto;
  comments: TestSessionCommentDto[];
  session: TestSessionDto;
  results: ValimateTestCaseResultDto[];
  history: TestSessionStatusHistoryDto[];
  declarationOfConformity: DeclarationOfConformityDto;
}
interface SessionSummaryPatch {
  user: UserDto;
  comments: TestSessionCommentDto[];
  session: TestSessionDto;
  history: TestSessionStatusHistoryDto[];
}
export interface SessionSummary {
  isLoading: boolean;
  isDownloading: boolean;
  hasFilesToDownload: boolean;
  choices: Step2VM;
  user: UserDto;
  commentModalMessage: string;
  commentModalVisible: boolean;
  commentModalPlaceholder: string;
  commentModalDestinationStatus: string;
  comments: TestSessionCommentDto[];
  session: TestSessionDto;
  results: ValimateTestCaseResultDto[];
  history: TestSessionStatusHistoryDto[];
  declarationOfConformity: DeclarationOfConformityDto;
}

const initialState: SessionSummary = {
  isLoading: false,
  isDownloading: false,
  hasFilesToDownload: false,
  choices: {},
  session: {},
  commentModalMessage: "",
  commentModalVisible: false,
  commentModalPlaceholder: "",
  commentModalDestinationStatus: "",
  comments: [],
  user: {},
  results: [],
  history: [],
  declarationOfConformity: {},
};

export const sessionSummarySlice = createSlice({
  name: "sessionSummary",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsDownloading: (state, action: PayloadAction<boolean>) => {
      state.isDownloading = action.payload;
    },
    setModalMessage: (state, action: PayloadAction<string>) => {
      state.commentModalMessage = action.payload;
    },
    openCommentModal: (state, action: PayloadAction<SessionSummaryModal>) => {
      state.commentModalVisible = true;
      state.commentModalDestinationStatus = action.payload.destinationStatus;
      state.commentModalPlaceholder = action.payload.placeholder;
    },
    closeCommentModal: (state) => {
      state.commentModalVisible = false;
      state.commentModalDestinationStatus = "";
      state.commentModalPlaceholder = "";
      state.commentModalMessage = "";
    },
    patchSession: (state, action: PayloadAction<SessionSummaryPatch>) => {
      state.user = action.payload.user;
      state.session.status = action.payload.session?.status;
      state.session.reviewedById = action.payload.session?.reviewedById;
      state.session.verifiedById = action.payload.session?.verifiedById;
      state.history = action.payload.history;
      state.comments = action.payload.comments;
    },
    setSessionSummmary: (
      state,
      action: PayloadAction<SessionSummaryInitial>
    ) => {
      state.user = action.payload.user;
      state.session = action.payload.session;
      state.hasFilesToDownload = action.payload.hasFilesToDownload;
      state.results = action.payload.results;
      state.history = action.payload.history;
      state.declarationOfConformity = action.payload.declarationOfConformity;
      state.choices = action.payload.choices;
      state.comments = action.payload.comments;
    },
  },
});

export const { actions } = sessionSummarySlice;

export const getSessionSummary = (sessionId: string) => (dispatch: any) => {
  dispatch(actions.setIsLoading(true));
  fetchTestSessionSummary(sessionId)
    .then((result: SessionSummaryVM) => {
      dispatch(
        actions.setSessionSummmary({
          user: result.user ?? {},
          session: result.session ?? {},
          hasFilesToDownload: result.hasFilesToDownload ?? false,
          results: result.results ?? [],
          history: result.history ?? [],
          declarationOfConformity:
            result.session?.declarationOfConformity ?? {},
          choices: result.choices ?? {},
          comments: result.comments ?? [],
        })
      );
    })
    .finally(() => dispatch(actions.setIsLoading(false)));
};

export const approveTestSession = (sessionId: string) => (dispatch: any) => {
  dispatch(actions.setIsLoading(true));
  approveSession(sessionId)
    .then((result: SessionSummaryVM) => {
      dispatch(
        actions.patchSession({
          user: result.user ?? {},
          session: result.session ?? {},
          history: result.history ?? [],
          comments: result.comments ?? [],
        })
      );
    })
    .finally(() => dispatch(actions.setIsLoading(false)));
};

export const closeCommentModal = () => (dispatch: any) => {
  dispatch(actions.closeCommentModal());
};

export const setIsDownloading = (isDownloading: boolean) => (dispatch: any) => {
  dispatch(actions.setIsDownloading(isDownloading));
};

export const changeModalMessage = (message: string) => (dispatch: any) => {
  dispatch(actions.setModalMessage(message));
};

export const openCommentModal =
  (destinationStatus: string, placeholder: string) => (dispatch: any) => {
    dispatch(
      actions.openCommentModal({
        placeholder: placeholder,
        destinationStatus: destinationStatus,
      })
    );
  };

export const changeTestSessionStatus =
  (sessionId: string) => (dispatch: any, getState: () => ApplicationState) => {
    let slice = getState().sessionSummarySlice;
    dispatch(actions.setIsLoading(true));
    setSessionStatus(
      sessionId,
      slice.commentModalDestinationStatus,
      slice.commentModalMessage
    )
      .then((result: SessionSummaryVM) => {
        dispatch(
          actions.patchSession({
            user: result.user ?? {},
            session: result.session ?? {},
            history: result.history ?? [],
            comments: result.comments ?? [],
          })
        );
      })
      .finally(() => {
        dispatch(closeCommentModal());
        dispatch(actions.setIsLoading(false));
      });
  };
